/**
 * Community API (client-safe)
 *
 * No .server suffix so client hooks can import without triggering
 * TanStack Start import-protection. Handlers run only on the server.
 *
 * Vertical slice: posts, comments, likes.
 * JWT auth for mutations; optional auth for reads (to attach user_has_liked).
 * Tables used: posts, comments, likes, profiles (implicit whitelist).
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

async function createAdminClient() {
  const { createClient } = await import("@supabase/supabase-js");
  const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
  const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!url || !serviceKey) {
    return {
      client: null as null,
      error:
        "Missing SUPABASE_URL or SUPABASE_SERVICE_ROLE_KEY on server.",
    };
  }
  const client = createClient(url, serviceKey, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
  return { client, error: null as null };
}

async function requireAuth(
  client: NonNullable<Awaited<ReturnType<typeof createAdminClient>>["client"]>,
  accessToken: string
) {
  const { data: { user }, error } = await client.auth.getUser(accessToken);
  if (error || !user) return { user: null, error: "Unauthorized" };
  return { user, error: null };
}

// ── Types (mirror source) ───────────────────────────────────────────────

export type PostAuthor = {
  username: string;
  full_name: string;
  avatar_url?: string;
};

export type PostRow = {
  id: string;
  user_id: string;
  title: string | null;
  content: string | null;
  created_at: string;
  updated_at: string;
  image_url?: string | null;
};

export type Post = PostRow & {
  author?: PostAuthor;
  likes_count: number;
  comments_count: number;
  user_has_liked?: boolean;
};

export type CommentRow = {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
};

export type Comment = CommentRow & {
  author?: { username?: string; full_name?: string; avatar_url?: string };
};

// ── getPosts ────────────────────────────────────────────────────────────

export const getPosts = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      limit: z.number().int().min(1).max(100).default(10),
      page: z.number().int().min(0).default(0),
      accessToken: jwtSchema.optional(),
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { posts: [], count: 0, error: initError };

    const from = data.page * data.limit;
    const to = from + data.limit - 1;

    const { count, error: countError } = await client
      .from("posts")
      .select("*", { count: "exact", head: true });
    if (countError) return { posts: [], count: 0, error: countError.message };

    const { data: rows, error } = await client
      .from("posts")
      .select("id, user_id, title, content, created_at, updated_at, image_url")
      .order("created_at", { ascending: false })
      .range(from, to);
    if (error || !rows) return { posts: [], count: count ?? 0, error: error?.message ?? "No data" };

    const userIds = [...new Set(rows.map((r) => r.user_id))];
    const { data: profiles } = await client
      .from("profiles")
      .select("id, username, full_name, avatar_url")
      .in("id", userIds.length ? userIds : [""]);

    const postIds = rows.map((r) => r.id);
    const { data: likesRows } = await client
      .from("likes")
      .select("post_id")
      .in("post_id", postIds.length ? postIds : [""]);
    const { data: commentsRows } = await client
      .from("comments")
      .select("post_id")
      .in("post_id", postIds.length ? postIds : [""]);

    const profileMap: Record<string, { username: string; full_name: string; avatar_url?: string }> = {};
    for (const p of profiles ?? []) {
      profileMap[p.id] = {
        username: p.username ?? "",
        full_name: p.full_name ?? "",
        avatar_url: p.avatar_url ?? undefined,
      };
    }
    const likeCount: Record<string, number> = {};
    for (const l of likesRows ?? []) likeCount[l.post_id] = (likeCount[l.post_id] ?? 0) + 1;
    const commentCount: Record<string, number> = {};
    for (const c of commentsRows ?? []) commentCount[c.post_id] = (commentCount[c.post_id] ?? 0) + 1;

    let userLikedSet: Set<string> = new Set();
    if (data.accessToken) {
      const { user } = await requireAuth(client, data.accessToken);
      if (user) {
        const { data: userLikes } = await client
          .from("likes")
          .select("post_id")
          .eq("user_id", user.id)
          .in("post_id", postIds);
        userLikedSet = new Set((userLikes ?? []).map((x) => x.post_id));
      }
    }

    const posts: Post[] = rows.map((r) => ({
      ...r,
      title: r.title ?? undefined,
      content: r.content ?? undefined,
      image_url: r.image_url ?? undefined,
      author: profileMap[r.user_id],
      likes_count: likeCount[r.id] ?? 0,
      comments_count: commentCount[r.id] ?? 0,
      user_has_liked: userLikedSet.has(r.id),
    }));

    return { posts, count: count ?? 0, error: null };
  });

// ── getPost ─────────────────────────────────────────────────────────────

export const getPost = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      postId: z.string().uuid(),
      accessToken: jwtSchema.optional(),
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };

    const { data: row, error } = await client
      .from("posts")
      .select("id, user_id, title, content, created_at, updated_at, image_url")
      .eq("id", data.postId)
      .single();
    if (error || !row) return { data: null, error: error?.message ?? "Post not found" };

    const [{ data: profile }, { count: likesCount }, { data: commentsRows }] = await Promise.all([
      client.from("profiles").select("username, full_name, avatar_url").eq("id", row.user_id).single(),
      client.from("likes").select("*", { count: "exact", head: true }).eq("post_id", data.postId),
      client.from("comments").select("id").eq("post_id", data.postId),
    ]);

    let user_has_liked = false;
    if (data.accessToken) {
      const { user } = await requireAuth(client, data.accessToken);
      if (user) {
        const { data: like } = await client
          .from("likes")
          .select("id")
          .eq("post_id", data.postId)
          .eq("user_id", user.id)
          .maybeSingle();
        user_has_liked = !!like;
      }
    }

    const post: Post = {
      ...row,
      title: row.title ?? undefined,
      content: row.content ?? undefined,
      image_url: row.image_url ?? undefined,
      author: profile
        ? { username: profile.username ?? "", full_name: profile.full_name ?? "", avatar_url: profile.avatar_url ?? undefined }
        : undefined,
      likes_count: likesCount ?? 0,
      comments_count: Array.isArray(commentsRows) ? commentsRows.length : 0,
      user_has_liked,
    };
    return { data: post, error: null };
  });

// ── getUserPosts ─────────────────────────────────────────────────────────

export const getUserPosts = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { posts: [], error: initError };

    const { user, error: authError } = await requireAuth(client, data.accessToken);
    if (!user) return { posts: [], error: authError };
    if (user.id !== data.userId) return { posts: [], error: "Forbidden" };

    const { data: rows, error } = await client
      .from("posts")
      .select("id, user_id, title, content, created_at, updated_at, image_url")
      .eq("user_id", data.userId)
      .order("created_at", { ascending: false });
    if (error || !rows?.length) return { posts: [], error: error?.message ?? null };

    const { data: profile } = await client
      .from("profiles")
      .select("username, full_name, avatar_url")
      .eq("id", data.userId)
      .single();
    const author: PostAuthor = {
      username: profile?.username ?? "",
      full_name: profile?.full_name ?? "",
      avatar_url: profile?.avatar_url ?? undefined,
    };

    const postIds = rows.map((r) => r.id);
    const { data: likesRows } = await client.from("likes").select("post_id").in("post_id", postIds);
    const { data: commentsRows } = await client.from("comments").select("post_id").in("post_id", postIds);
    const likeCount: Record<string, number> = {};
    for (const l of likesRows ?? []) likeCount[l.post_id] = (likeCount[l.post_id] ?? 0) + 1;
    const commentCount: Record<string, number> = {};
    for (const c of commentsRows ?? []) commentCount[c.post_id] = (commentCount[c.post_id] ?? 0) + 1;

    const posts: Post[] = rows.map((r) => ({
      ...r,
      title: r.title ?? undefined,
      content: r.content ?? undefined,
      image_url: r.image_url ?? undefined,
      author,
      likes_count: likeCount[r.id] ?? 0,
      comments_count: commentCount[r.id] ?? 0,
      user_has_liked: false,
    }));

    return { posts, error: null };
  });

// ── createPost ───────────────────────────────────────────────────────────

export const createPost = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      title: z.string().min(1).max(500),
      content: z.string().min(1).max(50000),
      image_url: z.string().url().optional().nullable(),
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };

    const { user, error: authError } = await requireAuth(client, data.accessToken);
    if (!user) return { data: null, error: authError };
    if (user.id !== data.userId) return { data: null, error: "Forbidden" };

    const { data: row, error } = await client
      .from("posts")
      .insert({
        title: data.title,
        content: data.content,
        image_url: data.image_url ?? null,
        user_id: data.userId,
      })
      .select()
      .single();
    if (error) return { data: null, error: error.message };
    const post: Post = {
      ...row,
      title: row.title ?? undefined,
      content: row.content ?? undefined,
      image_url: row.image_url ?? undefined,
      likes_count: 0,
      comments_count: 0,
    };

    // Publish Mycelium event for post creation
    const { publishMyceliumEvent } = await import("./_mycelium.server");
    await publishMyceliumEvent(client, {
      domain: "user",
      type: "post_created",
      userId: data.userId,
      payload: { postId: row.id, title: data.title },
    });

    return { data: post, error: null };
  });

// ── updatePost ───────────────────────────────────────────────────────────

export const updatePost = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      id: z.string().uuid(),
      title: z.string().min(1).max(500).optional(),
      content: z.string().min(1).max(50000).optional(),
      image_url: z.string().url().optional().nullable(),
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };

    const { user, error: authError } = await requireAuth(client, data.accessToken);
    if (!user) return { data: null, error: authError };
    if (user.id !== data.userId) return { data: null, error: "Forbidden" };

    const updatePayload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (data.title !== undefined) updatePayload.title = data.title;
    if (data.content !== undefined) updatePayload.content = data.content;
    if (data.image_url !== undefined) updatePayload.image_url = data.image_url;

    const { data: row, error } = await client
      .from("posts")
      .update(updatePayload)
      .eq("id", data.id)
      .eq("user_id", data.userId)
      .select()
      .single();
    if (error) return { data: null, error: error.message };
    const post: Post = {
      ...row,
      title: row.title ?? undefined,
      content: row.content ?? undefined,
      image_url: row.image_url ?? undefined,
      likes_count: 0,
      comments_count: 0,
    };
    return { data: post, error: null };
  });

// ── deletePost ───────────────────────────────────────────────────────────

export const deletePost = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      postId: z.string().uuid(),
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { success: false, error: initError };

    const { user, error: authError } = await requireAuth(client, data.accessToken);
    if (!user) return { success: false, error: authError };
    if (user.id !== data.userId) return { success: false, error: "Forbidden" };

    const { error } = await client
      .from("posts")
      .delete()
      .eq("id", data.postId)
      .eq("user_id", data.userId);
    if (error) return { success: false, error: error.message };

    // Publish Mycelium event for post deletion
    const { publishMyceliumEvent } = await import("./_mycelium.server");
    await publishMyceliumEvent(client, {
      domain: "user",
      type: "post_deleted",
      userId: data.userId,
      payload: { postId: data.postId },
    });

    return { success: true, error: null };
  });

// ── getComments ──────────────────────────────────────────────────────────

export const getComments = createServerFn({ method: "GET" })
  .inputValidator(z.object({ postId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { comments: [], error: initError };

    const { data: rows, error } = await client
      .from("comments")
      .select("id, post_id, user_id, content, created_at, updated_at")
      .eq("post_id", data.postId)
      .order("created_at", { ascending: true });
    if (error) return { comments: [], error: error.message };
    if (!rows?.length) return { comments: [], error: null };

    const userIds = [...new Set(rows.map((r) => r.user_id))];
    const { data: profiles } = await client
      .from("profiles")
      .select("id, username, full_name, avatar_url")
      .in("id", userIds);
    const profileMap: Record<string, { username?: string; full_name?: string; avatar_url?: string }> = {};
    for (const p of profiles ?? []) {
      profileMap[p.id] = {
        username: p.username ?? undefined,
        full_name: p.full_name ?? undefined,
        avatar_url: p.avatar_url ?? undefined,
      };
    }

    const comments: Comment[] = rows.map((r) => ({
      ...r,
      author: profileMap[r.user_id],
    }));
    return { comments, error: null };
  });

// ── createComment ────────────────────────────────────────────────────────

export const createComment = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      postId: z.string().uuid(),
      content: z.string().min(1).max(10000),
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };

    const { user, error: authError } = await requireAuth(client, data.accessToken);
    if (!user) return { data: null, error: authError };
    if (user.id !== data.userId) return { data: null, error: "Forbidden" };

    const { data: row, error } = await client
      .from("comments")
      .insert({ post_id: data.postId, user_id: data.userId, content: data.content })
      .select()
      .single();
    if (error) return { data: null, error: error.message };

    const { data: profile } = await client
      .from("profiles")
      .select("username, full_name, avatar_url")
      .eq("id", data.userId)
      .single();
    const comment: Comment = {
      ...row,
      author: profile
        ? { username: profile.username ?? undefined, full_name: profile.full_name ?? undefined, avatar_url: profile.avatar_url ?? undefined }
        : undefined,
    };

    // Publish Mycelium event for comment creation
    const { publishMyceliumEvent } = await import("./_mycelium.server");
    await publishMyceliumEvent(client, {
      domain: "user",
      type: "comment_created",
      userId: data.userId,
      payload: { postId: data.postId, commentId: row.id },
    });

    return { data: comment, error: null };
  });

// ── toggleLike ───────────────────────────────────────────────────────────

export const toggleLike = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      postId: z.string().uuid(),
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { liked: false, error: initError };

    const { user, error: authError } = await requireAuth(client, data.accessToken);
    if (!user) return { liked: false, error: authError };
    if (user.id !== data.userId) return { liked: false, error: "Forbidden" };

    const { data: existing } = await client
      .from("likes")
      .select("id")
      .eq("post_id", data.postId)
      .eq("user_id", data.userId)
      .maybeSingle();

    if (existing) {
      const { error: delError } = await client.from("likes").delete().eq("id", existing.id);
      if (delError) return { liked: false, error: delError.message };
      return { liked: false, error: null };
    }

    const { error: insertError } = await client
      .from("likes")
      .insert({ post_id: data.postId, user_id: data.userId });
    if (insertError) return { liked: false, error: insertError.message };
    return { liked: true, error: null };
  });
